import React, { useState, useEffect, forwardRef, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import SlotManagement from './components/SlotManagement';
import RoundManagement from './components/RoundManagement';
import ResidentManagement from './components/ResidentManagement';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from 'date-fns/locale/th';
import Navbar from './components/Navbar';
import ParkingMap from './components/ParkingMap';
import ReportManagement from './components/ReportManagement';
import RoleManagement from './components/RoleManagement';
import ZoneManagement from './components/ZoneManagement';
import AnnouncementManagement from './components/AnnouncementManagement';
import { API_URL } from './config';
import { Megaphone, X } from 'lucide-react';

registerLocale('th', th);

const CustomDateInput = forwardRef(({ value, onClick, disabled }, ref) => (
  <div
    onClick={disabled ? undefined : onClick}
    ref={ref}
    className={`w-full border rounded-xl flex items-center px-2 py-2 gap-1.5 text-left transition-all ${disabled
      ? 'bg-slate-50/80 border-slate-100 shadow-none cursor-default'
      : 'bg-white shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] cursor-pointer active:scale-[0.98]'
      }`}
  >
    <div className={`flex-shrink-0 transition-colors ${disabled ? 'text-slate-400' : 'text-blue-500 group-hover:text-blue-600'}`}>
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
      </svg>
    </div>
    <div className="flex flex-col w-full min-w-0">
      <span className={`text-[12px] font-black truncate leading-none ${disabled ? 'text-slate-500' : 'text-slate-700'}`}>
        {value}
      </span>
    </div>
    {!disabled && (
      <div className="text-slate-300 transition-colors flex-shrink-0 group-hover:text-blue-500 scale-90">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </div>
    )}
  </div>
));

function Dashboard({ token, setToken, role, username, permissions }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({ license: '', noLicense: false, sticker: '', noSticker: false, status: 'Normal', remark: '', vehicleType: 'Car', isHandbrakeReleased: null });
  const [error, setError] = useState(''); // Validation error state
  const [verification, setVerification] = useState({ loading: false, found: null });
  const [originalSticker, setOriginalSticker] = useState('');
  const [originalStatus, setOriginalStatus] = useState('');

  const [slots, setSlots] = useState([]);
  const [logs, setLogs] = useState({});
  const [rounds, setRounds] = useState([]);
  const [round, setRound] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [isRoundOpen, setIsRoundOpen] = useState(false);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(''); // Empty initially
  const [viewMode, setViewMode] = useState('Map'); // 'Grid' or 'Map'
  const [auditFilter, setAuditFilter] = useState('All'); // 'All', 'Audited', 'Pending'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // New state for custom delete confirm
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const roundRef = useRef(null);
  const summaryRef = useRef(null);

  const summaryData = useMemo(() => {
    let normal = 0, unclear = 0, empty = 0, noSticker = 0, visitor = 0;
    let minTime = null;
    let maxTime = null;

    Object.values(logs).forEach(log => {
      if (log.status === 'Normal') normal++;
      else if (log.status === 'Unclear') unclear++;
      else if (log.status === 'Empty') empty++;
      else if (log.status === 'No-Sticker') noSticker++;
      else if (log.status === 'Visitor') visitor++;

      if (log.created_at) {
        const t = new Date(log.created_at.replace(' ', 'T')).getTime();
        if (!isNaN(t)) {
          if (!minTime || t < minTime) minTime = t;
          if (!maxTime || t > maxTime) maxTime = t;
        }
      }
    });

    const total = Object.keys(logs).length;
    const issues = noSticker + unclear;

    let startTimeStr = '-';
    let endTimeStr = '-';
    let durationStr = '-';

    if (minTime && maxTime) {
      startTimeStr = new Date(minTime).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.';
      endTimeStr = new Date(maxTime).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.';

      const diffMins = Math.max(0, Math.floor((maxTime - minTime) / 60000));
      if (diffMins < 60) {
        durationStr = `${diffMins} นาที`;
      } else {
        durationStr = `${Math.floor(diffMins / 60)} ชม. ${diffMins % 60} นาที`;
      }
    }

    return { normal, unclear, empty, noSticker, visitor, total, issues, startTimeStr, endTimeStr, durationStr };
  }, [logs]);

  const generateSummaryImageBlob = async () => {
    if (!summaryRef.current) return null;
    try {
      // Ensure all web fonts (like Sarabun) are fully loaded
      if (document.fonts) {
        await document.fonts.ready;
      }
      
      // Small delay to ensure any potential layout shifts or animations are settled
      await new Promise(resolve => setTimeout(resolve, 600));

      const originalRect = summaryRef.current.getBoundingClientRect();
      const originalWidth = originalRect.width || 340;

      const canvas = await html2canvas(summaryRef.current, {
        scale: 2, // Scale 2 is highly stable and sufficient for 2x High-DPI rendering
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        onclone: (clonedDoc) => {
          const el = clonedDoc.querySelector('[data-summary-container]');
          if (el) {
            // Force stable styles for capture matching original display width
            el.style.boxShadow = 'none';
            el.style.borderRadius = '20px';
            el.style.width = `${originalWidth}px`;
            el.style.padding = '24px';
            el.style.transform = 'none';
            el.style.transition = 'none';
            el.style.animation = 'none';

            // Fix any children that might have inherited weird properties
            const allElements = el.querySelectorAll('*');
            allElements.forEach(item => {
              item.style.transition = 'none';
              item.style.animation = 'none';
              item.style.transform = 'none';
            });
          }
        }
      });

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 1.0);
      });
    } catch (err) {
      console.error("Failed to generate image blob:", err);
      return null;
    }
  };

  const handleDownloadSummary = async () => {
    if (isGeneratingImage) return;

    try {
      setIsGeneratingImage(true);
      const blob = await generateSummaryImageBlob();

      if (!blob) {
        throw new Error("Canvas is empty");
      }

      const safeDate = selectedDate.replace(/-/g, '');
      const filename = `audit_summary_${safeDate}.png`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });

      document.body.appendChild(link);
      link.dispatchEvent(clickEvent);

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 200);

      setIsGeneratingImage(false);
    } catch (err) {
      console.error("Failed to download image:", err);
      alert("ไม่สามารถบันทึกรูปภาพได้: " + (err.message || "เกิดข้อผิดพลาดในการประมวลผล"));
      setIsGeneratingImage(false);
    }
  };

  const handleShareSummary = async () => {
    if (isGeneratingImage) return;

    try {
      setIsGeneratingImage(true);
      const blob = await generateSummaryImageBlob();

      if (!blob) {
        throw new Error("Canvas is empty");
      }

      const safeDate = selectedDate.replace(/-/g, '');
      const filename = `audit_summary_${safeDate}.png`;
      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'สรุปผลการตรวจที่จอดรถ',
          text: `สรุปผลการตรวจรอบ ${rounds.find(r => String(r.id) === String(round))?.scheduled_time.substring(0, 5)} น. วันที่ ${new Date(selectedDate).toLocaleDateString('th-TH', { dateStyle: 'long' })}`,
        });
      } else {
        alert("เบราว์เซอร์ของคุณไม่รองรับการแชร์ไฟล์ในขณะนี้ กรุณาใช้การดาวน์โหลดแทน");
      }
      setIsGeneratingImage(false);
    } catch (err) {
      // User cancelling share is not an error we want to alert
      if (err.name !== 'AbortError') {
        console.error("Failed to share image:", err);
        alert("ไม่สามารถแชร์รูปภาพได้: " + (err.message || "เกิดข้อผิดพลาดในการประมวลผล"));
      }
      setIsGeneratingImage(false);
    }
  };

  // Derive Read-Only status from permissions
  const dashboardPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === 'dashboard');
  const canEdit = dashboardPerm ? (dashboardPerm.can_edit === 1 || dashboardPerm.can_edit === true) : true; // Default to true if not found (fallback)
  const isDashboardReadOnly = !canEdit;

  // Group and Sort Logic (Dynamic)
  const uniqueZones = useMemo(() => {
    return zones
      .filter(z => z.is_active)
      .sort((a, b) => a.display_order - b.display_order)
      .map(z => z.zone_name);
  }, [zones]);

  useEffect(() => {
    if (uniqueZones.length > 0 && !selectedZone) {
      setSelectedZone(uniqueZones[0]);
    }
  }, [uniqueZones, selectedZone]);

  // Define fetchLogs BEFORE using it in useEffect
  const fetchLogs = useCallback(async () => {
    if (!round) return; // Guard clause
    try {
      const res = await axios.get(`${API_URL}/api/logs/${round}`, {
        params: { date: selectedDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      const logMap = {};
      res.data.forEach(log => {
        logMap[log.slot_id] = log;
      });
      setLogs(logMap);
    } catch (err) {
      console.error('Fetch error:', err);
      // alert(`Failed to fetch ${API_URL}/api/logs/${round} - ${err.message}`);
    } finally {
      // setLoading(false); // Removed as setLoading is not defined
    }
  }, [round, selectedDate, token]);

  useEffect(() => {
    console.log('[DEBUG] API_URL:', API_URL);
    const timer = setTimeout(() => {
      // Fetch Slots
      axios.get(`${API_URL}/api/slots`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setSlots(res.data))
        .catch(err => console.error(err));

      // Fetch Rounds
      axios.get(`${API_URL}/api/rounds?active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setRounds(res.data);
          if (res.data.length > 0) {
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            let selectedRound = res.data[0];
            let maxMinutes = -1;

            res.data.forEach(r => {
              const [hours, minutes] = r.scheduled_time.split(':').map(Number);
              const roundMinutes = hours * 60 + minutes;
              if (currentMinutes >= roundMinutes) {
                if (roundMinutes > maxMinutes) {
                  maxMinutes = roundMinutes;
                  selectedRound = r;
                }
              }
            });
            setRound(selectedRound.id);
          }
        })
        .catch(err => console.error(err));

      // Fetch Zones
      axios.get(`${API_URL}/api/zones?active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          console.log('[DEBUG] Zones fetched successfully:', res.data.length);
          setZones(res.data);
        })
        .catch(err => {
          console.error('[DEBUG] Zones fetch error:', err.response ? `${err.response.status} ${err.response.statusText}` : err.message);
          console.error('[DEBUG] URL attempted:', `${API_URL}/api/zones?active=true`);
        });
    }, 50);

    return () => clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    if (round) fetchLogs();
  }, [round, selectedDate, fetchLogs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roundRef.current && !roundRef.current.contains(event.target)) {
        setIsRoundOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSlotName = (slotId) => {
    const slot = slots.find(s => String(s.id) === String(slotId));
    return slot ? slot.slot_name : slotId;
  };

  const handleSave = async () => {
    if (formData.status !== 'Empty' && !formData.license && !formData.noLicense) {
      setError('กรุณาระบุเลขทะเบียนรถ หรือเลือก "ไม่มีป้ายทะเบียน"');
      return;
    }

    if (formData.status === 'Normal') {
      if (!formData.sticker) {
        setError('กรุณาระบุเลขสติ๊กเกอร์');
        return;
      }
      if (!/^\d{8}$/.test(formData.sticker)) {
        setError('เลขสติ๊กเกอร์ต้องเป็นตัวเลข 8 หลัก');
        return;
      }
      // Block saving if status is Normal but sticker is not found or inactive
      const isInvalid = verification.found === false || (verification.found === true && !verification.is_active);
      if (isInvalid) {
        setError('ไม่พบข้อมูลสติ๊กเกอร์ กรุณาเลือกสถานะ "สติ๊กเกอร์ไม่ชัดเจน" หากต้องการบันทึก');
        return;
      }

      // Client-side duplicate check within current round's logs
      const duplicateSlotId = Object.keys(logs).find(slotId =>
        logs[slotId].sticker_no === formData.sticker && String(slotId) !== String(selectedSlot)
      );
      if (duplicateSlotId) {
        const slotName = getSlotName(duplicateSlotId);
        setError(`เลขสติ๊กเกอร์นี้ถูกใช้งานแล้วในช่อง ${slotName} ของรอบนี้ กรุณาติดต่อนิติบุคคลที่เกี่ยวข้อง`);
        return;
      }
    }

    if (formData.status === 'Unclear' && !formData.remark) {
      setError('กรณีสติ๊กเกอร์ไม่ชัดเจน กรุณาระบุหมายเหตุ');
      return;
    }

    const sObj = slots.find(s => String(s.id) === String(selectedSlot));
    const isDouble = sObj?.is_double_parking === 1 || sObj?.is_double_parking === true;

    if (isDouble && formData.status !== 'Empty' && formData.isHandbrakeReleased === null) {
      setError('กรุณาระบุว่าปลดเบรกมือแล้วหรือยัง');
      return;
    }

    try {
      const payload = {
        round_id: round,
        slot_id: selectedSlot,
        license_plate: formData.noLicense ? '' : formData.license,
        sticker_no: formData.noSticker ? '' : formData.sticker,
        status: formData.status,
        remark: formData.remark,
        log_date: selectedDate,
        created_by: username
      };

      // Only send vehicleType if status is not 'Empty'
      if (formData.status !== 'Empty') {
        payload.vehicle_type = formData.vehicleType;
        if (isDouble) {
          payload.is_handbrake = formData.isHandbrakeReleased ? 1 : 0;
        }
      }

      await axios.post(`${API_URL}/api/logs`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // const action = response.data.message.includes('updated') ? 'อัปเดต' : 'บันทึก';
      setSelectedSlot(null);
      setFormData({ license: '', noLicense: false, sticker: '', noSticker: false, status: 'Normal', remark: '', vehicleType: 'Car', isHandbrakeReleased: null });
      setError('');
      fetchLogs();
      setSuccessMessage('บันทึกข้อมูลช่องจอดเรียบร้อยแล้ว');
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 2000);
    } catch (err) {
      if (err.response && err.response.status === 401) return;
      alert('บันทึกไม่สำเร็จ');
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      const deleteUrl = `${API_URL}/api/logs/${round}/${selectedSlot}`;

      await axios.delete(deleteUrl, {
        params: { date: selectedDate },
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDeleteConfirm(false);
      setSelectedSlot(null);
      setFormData({ license: '', noLicense: false, sticker: '', noSticker: false, status: 'Normal', remark: '', vehicleType: 'Car', isHandbrakeReleased: null });
      setError('');
      fetchLogs();
      setSuccessMessage('ลบข้อมูลเรียบร้อยแล้ว');
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 2000);
    } catch (err) {
      if (err.response && err.response.status === 401) return;
      console.error('Delete Error:', err);
      setErrorMessage('ลบข้อมูลไม่สำเร็จ: ' + (err.response?.data?.message || err.message));
      setShowErrorPopup(true);
    }
  };

  const getSlotColor = (slotId) => {
    const slot = slots.find(s => String(s.id) === String(slotId));
    if (slot && (slot.is_activate === 0 || slot.is_activate === false)) {
      return 'bg-slate-100/50 border-slate-200/50 text-slate-300 opacity-40 grayscale pointer-events-none ring-0 shadow-none';
    }
    const log = logs[slotId];
    if (!log) return 'bg-white border-slate-200/80 text-slate-400 hover:border-blue-300 hover:shadow-md ring-1 ring-slate-100 shadow-sm';

    const status = log.status;
    if (status === 'Normal') return 'bg-emerald-50/70 border-emerald-200 text-emerald-800 ring-1 ring-emerald-500/10 shadow-sm hover:shadow-emerald-500/10 hover:border-emerald-300';
    if (status === 'Unclear') return 'bg-amber-50/70 border-amber-200 text-amber-800 ring-1 ring-amber-500/10 shadow-sm hover:shadow-amber-500/10 hover:border-amber-300';
    if (status === 'Empty') return 'bg-slate-50/30 border-slate-200/50 text-slate-400 shadow-none scale-[0.98] ring-1 ring-slate-100 hover:scale-100 transition-transform';
    if (status === 'No-Sticker') return 'bg-rose-50/70 border-rose-200 text-rose-800 ring-1 ring-rose-500/10 shadow-sm hover:shadow-rose-500/10 hover:border-rose-300';
    if (status === 'Visitor') return 'bg-sky-50/70 border-sky-200 text-sky-800 ring-1 ring-sky-500/10 shadow-sm hover:shadow-sky-500/10 hover:border-sky-300';
    return 'bg-slate-50 border-slate-300 text-slate-700 ring-1 ring-slate-200';
  };

  const handleSlotClick = (slotId) => {
    const slot = slots.find(s => String(s.id) === String(slotId));
    if (slot && (slot.is_activate === 0 || slot.is_activate === false)) {
      return;
    }
    setSelectedSlot(slotId);
    setError('');
    if (logs[slotId]) {
      const savedSticker = logs[slotId].sticker_no || '';
      const savedStatus = logs[slotId].status || 'Normal';
      setOriginalSticker(savedSticker);
      setOriginalStatus(savedStatus);
      setVerification({ loading: false, found: true, is_active: true });
      
      setFormData({
        license: logs[slotId].license_plate || '',
        noLicense: !logs[slotId].license_plate,
        sticker: savedSticker,
        noSticker: !savedSticker,
        status: savedStatus,
        remark: logs[slotId].remark || '',
        vehicleType: logs[slotId].vehicle_type || slot.vehicle_type || 'Car',
        isHandbrakeReleased: (logs[slotId].is_handbrake === undefined || logs[slotId].is_handbrake === null)
          ? null
          : (logs[slotId].is_handbrake === 1 || logs[slotId].is_handbrake === true)
      });
    } else {
      setOriginalSticker('');
      setOriginalStatus('Normal');
      setFormData({
        license: '',
        noLicense: false,
        sticker: '',
        noSticker: false,
        status: 'Normal',
        remark: '',
        vehicleType: slot.vehicle_type || 'Car',
        isHandbrakeReleased: null
      });
      setVerification({ loading: false, found: null });
    }
  };

  const isFormReadOnly = !token || (isDashboardReadOnly && selectedSlot && !!logs[selectedSlot]);

  useEffect(() => {
    const verifySticker = async () => {
      if (isFormReadOnly) {
        setVerification({ loading: false, found: null, is_active: null });
        setError('');
        return;
      }

      // If opening to view but hasn't changed sticker/status, do not trigger API check and clear warning error
      const hasChanged = formData.sticker !== originalSticker || formData.status !== originalStatus;
      if (!hasChanged) {
        setVerification({ loading: false, found: true, is_active: true });
        setError(prev => (prev && prev.includes('ไม่พบข้อมูลสติ๊กเกอร์')) ? '' : prev);
        return;
      }

      // Only verify if status is Normal
      if (formData.status === 'Normal' && formData.sticker.length === 8) {
        setVerification(prev => ({ ...prev, loading: true }));
        try {
          const res = await axios.get(`${API_URL}/api/vehicles/verify/${formData.sticker}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const found = !!res.data.found;
          const isActive = res.data.is_active === 1 || res.data.is_active === true || res.data.is_active === '1';

          setVerification({
            loading: false,
            found: found,
            is_active: isActive
          });

          const isInvalid = !found || !isActive;
          if (isInvalid) {
            setError('ไม่พบข้อมูลสติ๊กเกอร์ กรุณาเลือกสถานะ "สติ๊กเกอร์ไม่ชัดเจน" หากต้องการบันทึก');
          } else {
            setError(prev => (prev && prev.includes('ไม่พบข้อมูลสติ๊กเกอร์')) ? '' : prev);
          }
        } catch (err) {
          console.error(err);
          setVerification({ loading: false, found: null });
        }
      } else {
        setVerification({ loading: false, found: null, is_active: null });
        setError(prev => (prev && (prev.includes('ไม่พบข้อมูลสติ๊กเกอร์') || prev.includes('สติ๊กเกอร์นี้ถูกยกเลิก'))) ? '' : prev);
      }
    };
    const timer = setTimeout(verifySticker, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.sticker, formData.status, token, isFormReadOnly]);

  // Popup Announcement State & Effect
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
  const [popupImageErrors, setPopupImageErrors] = useState({});

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setCurrentPopupIndex(prev => (prev === popupQueue.length - 1 ? 0 : prev + 1));
    }
    if (isRightSwipe) {
      setCurrentPopupIndex(prev => (prev === 0 ? popupQueue.length - 1 : prev - 1));
    }
  };

  const activePopup = popupQueue[currentPopupIndex];

  useEffect(() => {
    const fetchAnnouncementsAndCheckPopup = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/announcements`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Find announcements that have show_popup = 1 and are active and scheduled currently
        const popupAnns = res.data.filter(ann => {
          const isPopup = ann.show_popup === 1 || ann.show_popup === true;
          const isActive = ann.is_active === 1 || ann.is_active === true;

          let isScheduledActive = true;
          const nowMs = Date.now();
          if (ann.start_date) {
            const start = new Date(ann.start_date).getTime();
            if (nowMs < start) isScheduledActive = false;
          }
          if (ann.end_date) {
            const end = new Date(ann.end_date).getTime();
            if (nowMs > end) isScheduledActive = false;
          }
          return isPopup && isActive && isScheduledActive;
        });

        if (popupAnns.length > 0) {
          const currentTime = Date.now();
          const eligiblePopups = popupAnns.filter(ann => {
            const updateTime = new Date(ann.updated_at || ann.created_at).getTime();
            const snoozeVal = localStorage.getItem(`seen_announcement_${ann.id}_${updateTime}`);
            if (snoozeVal === 'never') return false;
            if (snoozeVal) {
              const expireTime = parseInt(snoozeVal);
              if (!isNaN(expireTime) && currentTime < expireTime) {
                return false;
              }
            }
            return true;
          });

          if (eligiblePopups.length > 0) {
            setPopupQueue(eligiblePopups);
            setCurrentPopupIndex(0);
          }
        }
      } catch (err) {
        console.error('Failed to fetch announcements for popup check:', err);
      }
    };

    if (token) {
      fetchAnnouncementsAndCheckPopup();
    }
  }, [token]);

  const handleDismissPopup = (snoozeDays) => {
    if (!activePopup) return;

    const updateTime = new Date(activePopup.updated_at || activePopup.created_at).getTime();
    const key = `seen_announcement_${activePopup.id}_${updateTime}`;

    if (snoozeDays === -1) {
      // Dismiss forever
      localStorage.setItem(key, 'never');
    } else if (snoozeDays > 0) {
      // Dismiss for X days
      const expireTime = Date.now() + (snoozeDays * 24 * 60 * 60 * 1000);
      localStorage.setItem(key, expireTime.toString());
    }

    // Optional: Clean up old versions of this announcement's keys from localStorage
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const localKey = localStorage.key(i);
        if (localKey && localKey.startsWith(`seen_announcement_${activePopup.id}_`) && localKey !== key) {
          localStorage.removeItem(localKey);
        }
      }
    } catch (e) {
      console.warn('Failed to clean up old keys', e);
    }

    // Remove the current active popup from the queue
    const targetId = activePopup.id;
    setPopupQueue(prev => {
      const updatedQueue = prev.filter(ann => ann.id !== targetId);

      // Adjust index
      if (updatedQueue.length === 0) {
        setCurrentPopupIndex(0);
      } else if (currentPopupIndex >= updatedQueue.length) {
        setCurrentPopupIndex(updatedQueue.length - 1);
      }

      return updatedQueue;
    });
  };

  const handleCloseAllPopups = () => {
    setPopupQueue([]);
    setCurrentPopupIndex(0);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const todayStr = now.toLocaleDateString('en-CA');
      const currentActualDate = new Date().toLocaleDateString('en-CA');
      if (todayStr !== selectedDate && selectedDate === new Date(Date.now() - 86400000).toLocaleDateString('en-CA')) {
        setSelectedDate(currentActualDate);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  return (
    <div className="bg-[#F8FAFC] flex flex-col h-[100dvh] w-full overflow-hidden relative">
      <Navbar role={role} username={username} permissions={permissions} onLogout={() => setToken(null)} />

      {/* Ultra-Compact Glass Header */}
      <div className="flex-none z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200/50 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.1)] relative">
        <div className="w-full max-w-7xl mx-auto px-3 md:px-5 py-2.5">

          <div className="flex flex-col gap-2.5">
            {/* Top Row: Date, Round, Controls */}
            <div className="flex items-center justify-between gap-2.5">
              {/* Controls */}
              <div className="flex flex-1 gap-2 md:gap-2.5 min-w-0 max-w-[280px]">
                <div className="flex-1 min-w-0">
                  <DatePicker
                    selected={new Date(selectedDate)}
                    onChange={(date) => {
                      if (date) {
                        const offset = date.getTimezoneOffset();
                        const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
                        setSelectedDate(adjustedDate.toISOString().split('T')[0]);
                      }
                    }}
                    locale="th"
                    dateFormat="d MMM yy"
                    maxDate={new Date()}
                    disabled={isDashboardReadOnly}
                    customInput={<CustomDateInput disabled={isDashboardReadOnly} />}
                    wrapperClassName="w-full"
                    popperPlacement="bottom-start"
                  />
                </div>
                <div className="flex-1 min-w-0 relative" ref={roundRef}>
                  <div
                    onClick={() => { if (!isDashboardReadOnly) setIsRoundOpen(!isRoundOpen); }}
                    className={`w-full border rounded-xl flex items-center px-2 py-2 gap-1.5 text-left ${isDashboardReadOnly
                      ? 'bg-slate-50/80 border-slate-100 shadow-none cursor-default'
                      : `bg-white shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] active:scale-[0.98] cursor-pointer ${isRoundOpen ? '' : ''}`
                      }`}
                  >
                    <div className={`flex-shrink-0 transition-colors ${isDashboardReadOnly
                      ? 'text-slate-400'
                      : (isRoundOpen ? 'text-blue-600' : 'text-blue-500 group-hover:text-blue-600')
                      }`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex flex-col w-full min-w-0">
                      <span className={`text-[12px] font-black leading-none truncate ${isDashboardReadOnly ? 'text-slate-500' : 'text-slate-700'}`}>
                        {rounds.find(r => String(r.id) === String(round))?.scheduled_time.substring(0, 5) || 'เลือก'} น.
                      </span>
                    </div>
                    {!isDashboardReadOnly && (
                      <div className={`text-slate-300 transition-transform duration-200 scale-90 ${isRoundOpen ? 'rotate-180 text-blue-500' : 'group-hover:text-blue-500'}`}>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    )}
                  </div>

                  {isRoundOpen && (
                    <div className="absolute z-[60] mt-1.5 inset-x-0 bg-white border border-slate-100/80 rounded-2xl shadow-xl py-1 overflow-hidden animate-in fade-in zoom-in duration-200 origin-top">
                      <div className="max-h-[240px] overflow-y-auto scrollbar-hide py-1">
                        {rounds.map(r => (
                          <button
                            key={r.id}
                            onClick={() => {
                              setRound(r.id);
                              setIsRoundOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-[12px] font-black flex items-center justify-between transition-colors ${String(round) === String(r.id) ? 'bg-blue-50/80 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                          >
                            <span>{r.scheduled_time.substring(0, 5)} น.</span>
                            {String(round) === String(r.id) && (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex bg-[#F1F5F9] p-1 rounded-xl border border-slate-200/50 shrink-0 shadow-inner">
                <button
                  onClick={() => setViewMode('Grid')}
                  className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${viewMode === 'Grid' ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'}`}
                >
                  <svg className="w-4 h-4 sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                  <span className="hidden sm:inline tracking-tight">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode('Map')}
                  className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${viewMode === 'Map' ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'}`}
                >
                  <svg className="w-4 h-4 sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                  <span className="hidden sm:inline tracking-tight">Map</span>
                </button>
              </div>
            </div>

            {/* Bottom Row: Zones & Progress */}
            <div className="flex items-center justify-between gap-3 mt-1 px-1">
              {/* Zone Chips */}
              <div className="flex-1 overflow-x-auto scrollbar-hide py-1">
                <div className="flex gap-2 w-max">
                  {uniqueZones.map(zone => (
                    <button
                      key={zone}
                      onClick={() => { setSelectedZone(zone); setError(''); }}
                      className={`px-3.5 py-1.5 rounded-full text-[11px] font-black whitespace-nowrap transition-all active:scale-95 ${selectedZone === zone
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25 ring-1 ring-blue-500/50'
                        : 'bg-white/80 text-slate-500 hover:text-slate-700 hover:bg-white border-slate-200/50 shadow-sm ring-1 ring-slate-200/60'
                        }`}
                    >
                      {zone}
                    </button>
                  ))}
                </div>
              </div>

              {/* Progress / Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center justify-between gap-3 bg-white px-3 py-1.5 rounded-full shadow-sm ring-1 ring-slate-200/60 min-w-[100px]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span className="text-[9px] font-black tracking-widest text-slate-400 uppercase">Prog</span>
                  </div>
                  <span className={`text-[11px] font-black leading-none ${Object.keys(logs).length === slots.length && slots.length > 0 ? 'text-emerald-500 bg-emerald-50 rounded-md px-1 py-0.5' : 'text-slate-700'}`}>
                    {Object.keys(logs).length}/{slots.length}
                  </span>
                </div>

                {slots.length > 0 && Math.round((Object.keys(logs).length / slots.length) * 100) === 100 && (
                  <button
                    onClick={() => setShowSummaryModal(true)}
                    className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-3.5 py-1.5 rounded-full text-[11px] font-black shadow-md shadow-emerald-500/20 active:scale-95 transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    <span className="inline tracking-tight">รายงาน</span>
                  </button>
                )}
              </div>
            </div>

            {/* Audit Filter Row (Only for Grid) */}
            {viewMode === 'Grid' && (
              <div className="flex items-center gap-2 px-1 py-1.5 mt-0.5 overflow-x-auto scrollbar-hide border-t border-slate-100/50 animate-in fade-in slide-in-from-top-2">
                {[
                  { id: 'All', label: 'ทั้งหมด', icon: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16m-7 6h7" /></svg> },
                  { id: 'Audited', label: 'ตรวจแล้ว', icon: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg> },
                  { id: 'Pending', label: 'ยังไม่ตรวจ', icon: <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> }
                ].map(filter => {
                  const count = filter.id === 'All'
                    ? slots.filter(s => s.zone === selectedZone).length
                    : filter.id === 'Audited'
                      ? slots.filter(s => s.zone === selectedZone && logs[s.id]).length
                      : slots.filter(s => s.zone === selectedZone && !logs[s.id]).length;

                  return (
                    <button
                      key={filter.id}
                      onClick={() => setAuditFilter(filter.id)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black transition-all whitespace-nowrap active:scale-95 ${auditFilter === filter.id
                        ? (filter.id === 'Audited' ? 'bg-emerald-500 text-white shadow-emerald-200' : filter.id === 'Pending' ? 'bg-amber-500 text-white shadow-amber-200' : 'bg-blue-600 text-white shadow-blue-200') + ' shadow-md'
                        : 'bg-slate-100/80 text-slate-500 hover:bg-slate-200 shadow-sm ring-1 ring-slate-200/30'
                        }`}
                    >
                      {filter.icon}
                      <span>{filter.label}</span>
                      <span className="ml-0.5 opacity-60">({count})</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 relative w-full overflow-hidden bg-slate-50 flex flex-col">
        {viewMode === 'Map' ? (
          <div className="flex-1 relative w-full h-full m-0 p-0 overflow-hidden md:p-4">
            <ParkingMap
              slots={slots.filter(s => s.zone === selectedZone)}
              logs={logs}
              onSlotClick={handleSlotClick}
              selectedZone={selectedZone}
              isAdmin={false}
              zones={zones}
              onZoneChange={setSelectedZone}
              auditFilter={auditFilter}
              setAuditFilter={setAuditFilter}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-auto custom-scrollbar">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-3 md:gap-4">
                {slots
                  .filter(s => s.zone === selectedZone)
                  .filter(s => {
                    if (auditFilter === 'Audited') return !!logs[s.id];
                    if (auditFilter === 'Pending') return !logs[s.id];
                    return true;
                  })
                  .sort((a, b) => a.slot_name.localeCompare(b.slot_name, undefined, { numeric: true, sensitivity: 'base' }))
                  .map(s => {
                    const isActive = s.is_activate !== 0 && s.is_activate !== false;
                    const log = logs[s.id];
                    return (
                      <div
                        key={s.id}
                        onClick={() => isActive && handleSlotClick(s.id)}
                        className={`aspect-square md:aspect-square rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all duration-300 transform md:hover:-translate-y-1.5 md:hover:shadow-xl relative overflow-hidden border ${getSlotColor(s.id)}`}
                      >
                        <span className={`text-[16px] md:text-[18px] font-black tracking-tight leading-none text-center w-full px-1 ${!log ? 'text-slate-400' : ''}`}>{s.slot_name}</span>
                        {isActive && log && (
                          <div className="absolute bottom-1.5 right-1.5 left-1.5 px-0.5">
                            <div className={`py-1 ${log.status === 'Empty' ? 'bg-white/40' : 'bg-white/90'} backdrop-blur-md rounded-lg shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] font-black truncate text-center text-slate-800 leading-none text-[8px] sm:text-[9px] scale-95 border border-white/50`}>
                              {log.license_plate || log.status}
                            </div>
                          </div>
                        )}
                        {!isActive && (
                          <div className="absolute inset-0 flex items-center justify-center bg-slate-50/30 backdrop-blur-[1px]">
                            <span className="text-[7px] px-1.5 py-0.5 bg-slate-900/10 rounded-full font-black text-slate-400 uppercase tracking-widest rotate-[-15deg]">Off</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedSlot && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center z-[11000] md:p-4">
          <div
            className="absolute inset-0 animate-in fade-in duration-300"
            onClick={() => setSelectedSlot(null)}
          />
          <div className="bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-2xl w-full max-w-md md:max-w-[380px] max-h-[90vh] md:max-h-[85vh] flex flex-col animate-in slide-in-from-bottom-full md:slide-in-from-bottom-4 md:zoom-in-95 duration-300 overflow-hidden z-10 isolate mt-auto md:mt-0 relative pb-safe">
            {/* Mobile Sheet Drag Indicator */}
            <div className="w-full flex justify-center pt-3 pb-1 md:hidden shrink-0 touch-none">
              <div className="w-12 h-1.5 bg-slate-200 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex flex-col px-5 pt-2 pb-4 border-b border-slate-100 bg-white shrink-0">
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-col relative z-20">
                  <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1">{selectedZone}</span>
                  <h2 className="text-xl font-black text-slate-800 tracking-tight leading-none flex items-center gap-2">
                    ช่องจอด {getSlotName(selectedSlot)}
                    {(() => {
                      const sObj = slots.find(s => String(s.id) === String(selectedSlot));
                      const isDouble = sObj?.is_double_parking === 1 || sObj?.is_double_parking === true;
                      return isDouble && (
                        <span className="inline-flex items-center justify-center text-[10px] font-bold px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200/80 rounded-md">
                          ซ้อนคัน
                        </span>
                      );
                    })()}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedSlot(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors active:scale-90"
                  title="ปิด"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4 custom-scrollbar">
              {/* Warning Alert */}
              <div className="px-3 py-2 border border-amber-100 bg-amber-50/50 rounded-xl flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <p className="text-[10px] font-medium leading-relaxed text-amber-700">
                  หากพบรถต้องสงสัย หรือรถที่ไม่มีสติ๊กเกอร์ กรุณาแจ้งเจ้าหน้าที่ทันที
                </p>
              </div>

              {/* Section 1: Vehicle Info */}
              <div className="space-y-2.5">
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ประเภทรถ</label>
                    <div className="flex bg-slate-100/80 p-1 rounded-lg">
                      {['Car', 'Motorcycle'].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => { if (!isFormReadOnly && formData.status !== 'Empty') { setFormData({ ...formData, vehicleType: val }); setError(''); } }}
                          disabled={isFormReadOnly || formData.status === 'Empty'}
                          className={`flex-1 py-1 rounded-md text-[11px] font-black transition-all ${formData.vehicleType === val
                            ? (formData.status === 'Empty' ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white text-blue-600 shadow-sm')
                            : (formData.status === 'Empty' ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:bg-white/50 animate-in fade-in')
                            }`}
                        >
                          {val === 'Car' ? 'รถยนต์' : 'จักรยานยนต์'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ทะเบียนรถ</label>
                      <button
                        type="button"
                        onClick={() => { if (!isFormReadOnly && formData.status !== 'Empty') { setFormData({ ...formData, noLicense: !formData.noLicense, license: !formData.noLicense ? '' : formData.license }); setError(''); } }}
                        disabled={isFormReadOnly || formData.status === 'Empty'}
                        className={`text-[9px] font-bold uppercase tracking-tight flex items-center gap-1.5 transition-colors ${formData.noLicense ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${formData.noLicense ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}>
                          {formData.noLicense && <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        ไม่มีป้ายทะเบียน
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        placeholder={formData.noLicense ? "ไม่ต้องระบุทะเบียน" : "เช่น 2กข1234"}
                        className={`w-full text-[12px] font-bold px-3 py-2 rounded-lg border outline-none  ${error && !formData.license && !formData.noLicense
                          ? 'border-rose-300 bg-rose-50/30'
                          : 'border-slate-200 focus:border-slate-300'
                          } ${formData.noLicense || formData.status === 'Empty' ? 'bg-slate-50 text-slate-400 cursor-not-allowed border-slate-200' : 'bg-white text-slate-800'}`}
                        value={formData.license}
                        disabled={isFormReadOnly || formData.noLicense || formData.status === 'Empty'}
                        onChange={e => {
                          const cleanedValue = e.target.value.replace(/[^ก-๙0-9]/g, '');
                          setFormData({ ...formData, license: cleanedValue });
                          setError('');
                        }}
                      />
                      {formData.license && !formData.noLicense && !isFormReadOnly && (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-blue-50 flex items-center justify-center animate-in zoom-in duration-200">
                          <svg className="w-2.5 h-2.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>



              {/* Section 2: Sticker Status */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'มีสติ๊กเกอร์', value: 'Normal', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                    { label: 'ไม่ชัดเจน', value: 'Unclear', color: 'bg-amber-50 text-amber-700 border-amber-200' },
                    { label: 'Visitor', value: 'Visitor', color: 'bg-sky-50 text-sky-700 border-sky-200' },
                    { label: 'ไม่มีสติ๊กเกอร์', value: 'No-Sticker', color: 'bg-rose-50 text-rose-700 border-rose-200' },
                    { label: 'ไม่มีรถจอด', value: 'Empty', color: 'bg-slate-50 text-slate-600 border-slate-300' }
                  ].map((opt, idx) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { if (!isFormReadOnly) { setFormData({ ...formData, status: opt.value, noSticker: ['No-Sticker', 'Empty'].includes(opt.value), noLicense: opt.value === 'Empty', license: opt.value === 'Empty' ? '' : formData.license, sticker: ['No-Sticker', 'Visitor', 'Unclear', 'Empty'].includes(opt.value) ? '' : formData.sticker }); setError(''); } }}
                      className={`py-1.5 rounded-lg border text-[11px] font-black transition-all active:scale-[0.98] ${idx === 4 ? 'col-span-2' : ''} ${formData.status === opt.value
                        ? `${opt.color} ring-1 ring-black/5 shadow-sm`
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {(() => {
                  const sObj = slots.find(s => String(s.id) === String(selectedSlot));
                  const isDouble = sObj?.is_double_parking === 1 || sObj?.is_double_parking === true;
                  return isDouble && formData.status !== 'Empty' && (
                    <div className="space-y-1 animate-in slide-in-from-top-2 duration-300">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ตรวจสอบการจอดซ้อนคัน</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'ปลดเบรคมือแล้ว', value: true, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                          { label: 'ยังไม่ได้ปลดเบรคมือ', value: false, color: 'bg-rose-50 text-rose-700 border-rose-200' }
                        ].map((opt) => (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => { if (!isFormReadOnly) { setFormData({ ...formData, isHandbrakeReleased: opt.value }); setError(''); } }}
                            disabled={isFormReadOnly}
                            className={`py-1.5 rounded-lg border text-[11px] font-black transition-all active:scale-[0.98] ${formData.isHandbrakeReleased === opt.value
                              ? `${opt.color} ring-1 ring-black/5 shadow-sm`
                              : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                              }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {formData.isHandbrakeReleased === false && (
                        <div className="flex justify-end mt-1">
                          <div className="flex items-center gap-1.5 text-rose-600 text-[11px] font-semibold animate-in fade-in slide-in-from-top-1 duration-200">
                            <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            <span>กรุณาแจ้งเจ้าหน้าที่ทันที</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {formData.status === 'Normal' && (
                  <div className="space-y-1 animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">เลขสติ๊กเกอร์ (8 หลัก)</label>
                    </div>
                    <div className="relative">
                      <input
                        placeholder="กรอกตัวเลข 8 หลัก"
                        className={`w-full text-[12px] font-bold px-3 py-2 rounded-lg border outline-none ${(error && error.includes('สติ๊กเกอร์')) || ((verification.found === false || (verification.found === true && !verification.is_active)) && formData.status === 'Normal' && formData.sticker.length === 8)
                          ? 'border-rose-300 bg-rose-50/30'
                          : 'border-slate-200 focus:border-slate-300'
                          } ${isFormReadOnly ? 'bg-slate-50 text-slate-500 cursor-not-allowed opacity-60' : 'bg-white text-slate-800'}`}
                        value={formData.sticker}
                        disabled={isFormReadOnly}
                        maxLength={8}
                        inputMode="numeric"
                        onChange={e => {
                          let val = e.target.value.replace(/\D/g, '');
                          if (val.length <= 8) setFormData({ ...formData, sticker: val });
                          setError('');
                        }}
                      />
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        {verification.loading && <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>}
                        {verification.found && verification.is_active && !verification.loading && (
                          <div className="w-4 h-4 rounded-full bg-green-50 flex items-center justify-center shadow-sm animate-in zoom-in duration-200">
                            <svg className="w-2.5 h-2.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                          </div>
                        )}
                        {(verification.found === false || (verification.found === true && !verification.is_active)) && !verification.loading && formData.sticker.length === 8 && (
                          <div className="w-4 h-4 rounded-full bg-rose-50 flex items-center justify-center shadow-sm animate-in zoom-in duration-200">
                            <svg className="w-2.5 h-2.5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 3: Additional Detail */}
              <div className="space-y-3">
                <div className="space-y-1 font-sans">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                    <span>หมายเหตุ</span>
                    {formData.status === 'Unclear' && <span className="text-rose-500 text-[9px] font-black">* จำเป็น</span>}
                  </label>
                  <textarea
                    placeholder={formData.status === 'Unclear' ? "กรุณาระบุเหตุผลที่สติ๊กเกอร์ไม่ชัดเจน" : "ระบุหมายเหตุเพิ่มเติม (ถ้ามี)"}
                    className={`w-full text-[12px] px-3 py-1.5 rounded-lg border outline-none resize-none  ${error && formData.status === 'Unclear' && !formData.remark
                      ? 'border-rose-300 bg-rose-50/30'
                      : 'border-slate-200 focus:border-slate-300'
                      }`}
                    rows={2}
                    value={formData.remark}
                    disabled={isFormReadOnly}
                    onChange={e => { setFormData({ ...formData, remark: e.target.value }); setError(''); }}
                  />
                </div>
              </div>
            </div>

            {/* Error Area */}
            {error && (
              <div className="mx-4 mb-2 p-2 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 animate-in slide-in-from-bottom-2 duration-300 shadow-sm">
                <div className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <span className="text-xs leading-tight">{error}</span>
              </div>
            )}

            {/* Footer Buttons */}
            <div className="p-4 md:p-5 border-t border-slate-100 bg-slate-50/80 shrink-0 select-none">
              <div className="flex gap-2">
                {!isFormReadOnly && logs[selectedSlot] && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-rose-200 text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-all active:scale-95 group shadow-sm shrink-0"
                    title="ลบข้อมูล"
                  >
                    <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                )}
                {!isFormReadOnly && (
                  <button
                    type="button"
                    onClick={handleSave}
                    className={`flex-1 h-10 px-6 rounded-xl text-white text-[13px] font-black shadow-xl transition-all active:scale-[0.98] ${logs[selectedSlot]
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-500/30 ring-2 ring-amber-500/20'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/30 ring-2 ring-blue-500/20'
                      }`}
                  >
                    {logs[selectedSlot] ? 'บันทึกแก้ไข' : 'บันทึก'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Custom Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
            <div className="p-10 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 ring-8 ring-rose-50/50">
                <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">ยืนยันการลบข้อมูล?</h3>
              <p className="text-[13px] text-slate-400 mt-2">ข้อมูลนี้จะถูกลบถาวร ไม่สามารถกู้คืนได้</p>
              <div className="flex gap-3 w-full mt-10">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-3 border border-slate-100 text-slate-500 rounded-2xl font-black text-[14px] active:scale-95 hover:bg-slate-50 shadow-sm transition-all"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-3 bg-rose-500 text-white rounded-2xl font-black text-[14px] shadow-lg shadow-rose-200 active:scale-95 hover:bg-rose-600 transition-all tracking-wide"
                >
                  ลบข้อมูล
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Image Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setShowSummaryModal(false)}
          />
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden z-10 animate-in zoom-in-95 fade-in duration-200 border border-slate-100 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center px-4 py-3 border-b border-slate-100 bg-white sticky top-0">
              <h2 className="text-sm font-bold text-slate-800 tracking-tight">สรุปผลการตรวจ</h2>
              <button onClick={() => setShowSummaryModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 ">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-4 overflow-y-auto w-full flex flex-col items-center justify-center bg-slate-50/50">
              {/* SLIP TO RENDER */}
              <div ref={summaryRef} data-summary-container className="w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-6 shrink-0 flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-emerald-500"></div>

                <div className="flex flex-col items-center justify-center mb-4 mt-1 border-b border-dashed border-slate-100 pb-4">
                  <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-2 border border-emerald-100/50 shadow-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h3 className="text-base font-black text-slate-800 leading-tight tracking-tight mt-0.5">ตรวจสอบสำเร็จ</h3>
                  <p className="text-[8px] font-bold text-slate-400 mt-0.5 uppercase tracking-widest">Parking Audit System</p>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                    <span className="text-slate-500 font-medium text-xs">วันที่ตรวจ</span>
                    <span className="font-bold text-slate-800 text-xs">{new Date(selectedDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                    <span className="text-slate-500 font-medium text-xs">รอบเวลา</span>
                    <span className="font-bold text-slate-800 text-xs">{rounds.find(r => String(r.id) === String(round))?.scheduled_time.substring(0, 5)} น.</span>
                  </div>
                  <div className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                    <span className="text-slate-500 font-medium text-xs">ผู้ตรวจสอบ</span>
                    <span className="font-bold text-slate-800 text-xs">{username}</span>
                  </div>
                  <div className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                    <span className="text-slate-500 font-medium text-xs">สถานะ</span>
                    <span className="font-bold text-emerald-600 text-xs flex items-center gap-1">
                      ครบทุกช่องจอด (100%)
                    </span>
                  </div>
                </div>

                <div className="mt-4 border-b border-dashed border-slate-200 pb-3 flex justify-between items-stretch">
                  <div className="flex-1 text-center py-0.5">
                    <div className="text-[10px] font-bold text-slate-400 mb-0.5">เริ่มตรวจ</div>
                    <div className="text-xs font-black text-slate-700">{summaryData.startTimeStr}</div>
                  </div>
                  <div className="flex-1 text-center border-x border-slate-100 py-0.5">
                    <div className="text-[10px] font-bold text-slate-400 mb-0.5">เสร็จสิ้น</div>
                    <div className="text-xs font-black text-slate-700">{summaryData.endTimeStr}</div>
                  </div>
                  <div className="flex-1 text-center py-0.5">
                    <div className="text-[10px] font-bold text-slate-400 mb-0.5">ใช้เวลา</div>
                    <div className="text-xs font-black text-blue-600">{summaryData.durationStr}</div>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100">
                  <div className="text-center mb-4 leading-relaxed">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      สรุปผลยอดรวม <span className="text-slate-700 font-black mx-1 text-[12px]">{summaryData.total}</span> ช่องจอด
                    </span>
                  </div>

                  <div className="flex mb-2">
                    <div className="flex-1 bg-emerald-50 rounded-lg py-2.5 px-1 text-center text-emerald-700 border border-emerald-100/50 mr-2">
                      <div className="text-lg font-black leading-none mb-1.5">{summaryData.normal}</div>
                      <div className="text-[8px] font-bold uppercase tracking-tight opacity-80">มีสติ๊กเกอร์</div>
                    </div>
                    <div className="flex-1 bg-slate-50 rounded-lg py-2.5 px-1 text-center text-slate-600 border border-slate-200/50">
                      <div className="text-lg font-black leading-none mb-1.5">{summaryData.empty}</div>
                      <div className="text-[8px] font-bold uppercase tracking-tight opacity-80">ไม่มีรถจอด</div>
                    </div>
                  </div>

                  {(summaryData.noSticker > 0 || summaryData.unclear > 0 || summaryData.visitor > 0) && (
                    <div className="mt-3 bg-[#FFF1F2] rounded-xl p-3 border border-rose-100">
                      <p className="text-[8px] font-black text-rose-500 uppercase tracking-widest mb-2 text-center">พบปัญหา / อื่นๆ ({summaryData.issues + summaryData.visitor})</p>
                      <div className="flex mt-1">
                        <div className="flex-1 bg-white rounded-md py-2.5 px-1 text-center text-rose-600 border border-rose-100 shadow-sm mr-1.5">
                          <div className="text-sm font-black leading-none mb-1">{summaryData.noSticker}</div>
                          <div className="text-[7px] font-bold">ไม่มีสติ๊กเกอร์</div>
                        </div>
                        <div className="flex-1 bg-white rounded-md py-2.5 px-1 text-center text-amber-600 border border-amber-100 shadow-sm mr-1.5">
                          <div className="text-sm font-black leading-none mb-1">{summaryData.unclear}</div>
                          <div className="text-[7px] font-bold">ไม่ชัดเจน</div>
                        </div>
                        <div className="flex-1 bg-white rounded-md py-2.5 px-1 text-center text-sky-600 border border-sky-100 shadow-sm">
                          <div className="text-sm font-black leading-none mb-1">{summaryData.visitor}</div>
                          <div className="text-[7px] font-bold">Visitor</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 text-center pt-2 border-t border-slate-50 opacity-60">
                  <p className="text-[8px] font-medium text-slate-400">สร้างเมื่อ {new Date().toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })} น. โดย {username}</p>
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-slate-100 bg-slate-50/10">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleDownloadSummary}
                  disabled={isGeneratingImage}
                  className={`py-2 px-3 rounded-xl text-[10px] font-bold shadow-sm  flex items-center justify-center gap-2 active:scale-[0.98] ${isGeneratingImage ? 'bg-slate-50 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-slate-200/40'}`}
                >
                  <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  <span>ดาวน์โหลด</span>
                </button>
                <button
                  onClick={handleShareSummary}
                  disabled={isGeneratingImage}
                  className={`py-2 px-3 rounded-xl text-[10px] font-bold shadow-md  flex items-center justify-center gap-2 active:scale-[0.98] ${isGeneratingImage ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200' : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-500/20'}`}
                >
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span>แชร์รูปภาพ</span>
                </button>
              </div>

              {isGeneratingImage && (
                <div className="mt-2.5 flex items-center justify-center gap-2.5">
                  <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">Processing</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="fixed inset-0 z-[2200] flex items-center justify-center p-4 text-center">
          <div
            className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
            onClick={() => setShowSuccessPopup(false)}
          />
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
            <div className="p-10 flex flex-col items-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
              <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{successMessage}</p>

              <button
                onClick={() => setShowSuccessPopup(false)}
                className="mt-8 w-full py-2.5 bg-blue-600 text-white rounded-2xl font-black text-[14px] tracking-wide active:scale-95 hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
              >
                ตกลง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup Modal */}
      {showErrorPopup && (
        <div className="fixed inset-0 z-[2200] flex items-center justify-center p-4 text-center">
          <div
            className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
            onClick={() => setShowErrorPopup(false)}
          />
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
            <div className="p-10 flex flex-col items-center">
              <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
              <h3 className="text-xl font-black text-rose-600 tracking-tight">เกิดข้อผิดพลาด</h3>
              <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{errorMessage}</p>

              <button
                onClick={() => setShowErrorPopup(false)}
                className="mt-8 w-full py-2.5 bg-slate-800 text-white rounded-2xl font-black text-[14px] tracking-wide active:scale-95 hover:bg-slate-900 shadow-lg shadow-slate-200 transition-all"
              >
                เข้าใจแล้ว
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Popup Modal with Carousel */}
      {popupQueue.length > 0 && activePopup && (
        <div className="fixed inset-0 z-[2500] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={handleCloseAllPopups}
          />
          <div
            className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-200 border border-slate-100 flex flex-col relative group max-h-[85vh]"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >


            {/* Close All x button top right */}
            <button
              onClick={handleCloseAllPopups}
              className="absolute top-4 right-4 z-30 p-1.5 bg-black/15 text-white hover:bg-black/30 rounded-full transition-all backdrop-blur-sm cursor-pointer border-none shadow-sm"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Image banner (renders only if image_url exists and hasn't failed to load) */}
            {activePopup.image_url && !popupImageErrors[activePopup.image_url] && (
              <div className="w-full bg-white relative flex justify-center items-center overflow-hidden max-h-[55vh] shrink-0">
                <img
                  src={`${API_URL}${activePopup.image_url}`}
                  alt={activePopup.title}
                  className="w-full max-h-[55vh] object-contain select-none"
                  onError={() => {
                    setPopupImageErrors(prev => ({ ...prev, [activePopup.image_url]: true }));
                  }}
                />
              </div>
            )}

            <div className={`p-6 pb-8 flex-1 flex flex-col min-h-0 ${(!activePopup.image_url || popupImageErrors[activePopup.image_url]) ? 'pt-10' : ''}`}>

              {/* Title Section with Inline Icon (if no image or image failed) */}
              {(!activePopup.image_url || popupImageErrors[activePopup.image_url]) ? (
                <div className="flex items-center gap-2.5 mb-3 shrink-0 pr-8">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center border border-blue-100 shrink-0">
                    <Megaphone className="w-4 h-4 rotate-[-15deg]" />
                  </div>
                  <h3 className="text-base font-black text-slate-800 leading-snug">{activePopup.title}</h3>
                </div>
              ) : (
                <h3 className="text-base font-black text-slate-800 leading-snug mb-2 shrink-0">{activePopup.title}</h3>
              )}

              <p className="text-xs font-semibold text-slate-500 leading-relaxed whitespace-pre-wrap overflow-y-auto pr-1 mb-4 select-text flex-1 min-h-0">
                {activePopup.content}
              </p>

              {/* Bottom Actions Area */}
              <div className="mt-auto shrink-0 flex flex-col">

                {/* Navigation Row (Dots Only) */}
                {popupQueue.length > 1 && (
                  <div className="flex items-center justify-center pt-2 pb-2">
                    <div className="flex gap-1.5">
                      {popupQueue.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentPopupIndex(idx)}
                          className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer border-none ${idx === currentPopupIndex ? 'bg-blue-600 w-3' : 'bg-slate-200 hover:bg-slate-300'
                            }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Snooze Row */}
                {activePopup.popup_snooze_days !== 0 && (
                  <div className="flex justify-center pt-3">
                    <button
                      onClick={() => handleDismissPopup(activePopup.popup_snooze_days)}
                      className="py-1 text-[11px] font-black text-slate-400 hover:text-black transition-all cursor-pointer border-none bg-transparent text-center"
                    >
                      {activePopup.popup_snooze_days === -1
                        ? 'ไม่ต้องแสดงประกาศนี้อีก'
                        : `ไม่ต้องแสดงอีก ${activePopup.popup_snooze_days} วัน`
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [role, setRole] = useState(localStorage.getItem('role'));
  const [username, setUsername] = useState(localStorage.getItem('username'));
  const [permissions, setPermissions] = useState([]);
  const [permissionsError, setPermissionsError] = useState(false);

  // Guards to prevent redundant fetches
  const hasFetchedPerms = useRef(false);
  const hasVerifiedSession = useRef(false);

  const setTokenAndStorage = (newToken, newRole, newUsername) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    } else {
      localStorage.removeItem('token');
      setToken(null);
    }
    if (newRole) {
      localStorage.setItem('role', newRole);
      setRole(newRole);
    } else {
      localStorage.removeItem('role');
      setRole(null);
    }
    if (newUsername) {
      localStorage.setItem('username', newUsername);
      setUsername(newUsername);
    } else {
      localStorage.removeItem('username');
      setUsername(null);
    }
    if (!newToken) {
      setPermissions([]);
      setPermissionsError(false);
    }
  };

  const fetchPermissions = useCallback(async (force = false) => {
    if (!token || !role) return;
    if (hasFetchedPerms.current && !force) return;

    try {
      setPermissionsError(false);
      hasFetchedPerms.current = true;
      const res = await axios.get(`${API_URL}/api/roles/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Store all permissions
      setPermissions(res.data);
    } catch (err) {
      console.error('Failed to fetch permissions', err);
      hasFetchedPerms.current = false;
      // Fallback to empty array to avoid infinite loading
      setPermissions([]);
      setPermissionsError(true);
    }
  }, [token, role]);

  useEffect(() => {
    // Reset guards when token changes (e.g. login/logout)
    hasFetchedPerms.current = false;
    hasVerifiedSession.current = false;
    setPermissionsError(false);
    hasVerifiedSession.current = false;

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          if (window.location.pathname !== '/login') {
            setTokenAndStorage(null);
          }
        }
        return Promise.reject(error);
      }
    );

    const verifySession = async () => {
      try {
        await axios.get(`${API_URL}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchPermissions(true);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          setTokenAndStorage(null);
        }
      }
    };

    const timer = setTimeout(() => {
      if (token) verifySession();
    }, 50);

    return () => {
      axios.interceptors.response.eject(interceptor);
      clearTimeout(timer);
    };
  }, [token, fetchPermissions]);

  const ProtectedRoute = ({ children, menuKey }) => {
    if (!token) return <Navigate to="/login" />;

    if (permissionsError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50/50 p-6">
          <div className="bg-white rounded-[2rem] p-8 shadow-2xl max-w-sm w-full text-center border border-slate-100/80 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-8 ring-amber-50/50">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-black text-slate-800 tracking-tight">ขออภัยเกิดข้อผิดพลาด</h3>
            <p className="text-[12px] font-semibold text-slate-400 mt-2 leading-relaxed">
              กรุณาลองใหม่อีกครั้ง
            </p>
            <div className="flex gap-3 w-full mt-8">
              <button
                onClick={() => setTokenAndStorage(null)}
                className="flex-1 py-3 border border-slate-100 text-slate-500 rounded-2xl font-black text-[13px] active:scale-95 hover:bg-slate-50 shadow-sm transition-all"
              >
                ออกจากระบบ
              </button>
              <button
                onClick={() => fetchPermissions(true)}
                className="flex-1 py-3 bg-blue-600 text-white rounded-2xl font-black text-[13px] shadow-lg shadow-blue-200 active:scale-95 hover:bg-blue-700 transition-all tracking-wide"
              >
                ลองใหม่
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (permissions.length === 0) return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50/50">
        <div className="w-12 h-12 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4 shadow-sm"></div>
        <span className="text-[12px] font-black text-slate-400 uppercase tracking-[0.3em] animate-pulse">Loading</span>
      </div>
    );

    // Check permission for current role
    const perm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === menuKey);
    if (perm && perm.is_visible) {
      return children;
    }

    const firstPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.is_visible);
    if (firstPerm) {
      const pathTo = {
        dashboard: '/', users: '/users', slots: '/slots', rounds: '/rounds',
        residents: '/residents', reports: '/reports', roles: '/roles', zones: '/zones',
        announcements: '/announcements'
      }[firstPerm.menu_key] || '/';
      return <Navigate to={pathTo} />;
    }
    return <Navigate to="/login" />;
  };

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login onLogin={setTokenAndStorage} />} />
        <Route path="/" element={<ProtectedRoute menuKey="dashboard"><Dashboard token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute menuKey="users"><UserManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/slots" element={<ProtectedRoute menuKey="slots"><SlotManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/rounds" element={<ProtectedRoute menuKey="rounds"><RoundManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/residents" element={<ProtectedRoute menuKey="residents"><ResidentManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute menuKey="reports"><ReportManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/roles" element={<ProtectedRoute menuKey="roles"><RoleManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} onRefresh={fetchPermissions} /></ProtectedRoute>} />
        <Route path="/zones" element={<ProtectedRoute menuKey="zones"><ZoneManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
        <Route path="/announcements" element={<ProtectedRoute menuKey="announcements"><AnnouncementManagement token={token} role={role} username={username} setToken={setTokenAndStorage} permissions={permissions} /></ProtectedRoute>} />
      </Routes>
    </Router>
  );
}

export default App;